import {
  useCreateDealDocumentMutation,
  useDeleteDealDocumentMutation,
  useEditFactorsDealMutation,
  useGetCommissionCalculationTypesQuery,
  useGetFactorsConditionsQuery,
  useGetFactorsSettingsQuery,
  useGetRecourseTypesQuery,
  useLazyGetCommissionCalculationTypesQuery,
  useRejectDealRequestMutation,
  useSignDealDocumentMutation,
} from '@/app/api';
import {
  DealRequest,
  DealRequestDocument,
  DefaultDictionary,
} from '@/app/api/types';
import {
  getBase64,
  getFetchEror,
  getInternationalDealKind,
} from '@/app/helpers';
import { useAppSelector } from '@/app/hooks';
import useDebounce from '@/app/hooks/useDebounce';
import {
  useLazyGetDocBase64Query,
  useLazyGetStringToSignQuery,
} from '@/entities/didox/api';
import { eimzo } from '@/entities/eimzo';
import { ClarificationModalRef } from '@/features/factor-request-clarification-modal/types';
import { useNotification } from '@/shared/contexts/NotificationContext';
import { FormDatePicker } from '@/shared/inputs/form-date-picker';
import { FormNumericInput } from '@/shared/inputs/form-numeric-input';
import FormSelectInput from '@/shared/inputs/form-select-input';
import { CommentModal } from '@/shared/modals/comment-modal';
import { CommentModalRef } from '@/shared/modals/comment-modal/types';
import { UIFormLabel } from '@/shared/ui/ui-form-label';
import { FactorAddDocument } from '@/widgets/factor-add-document';
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  SxProps,
  Tooltip,
  Typography,
} from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  checkIsClosedFactoringDeal,
  checkIsContractAndRegistrySentInDeal,
  checkIsDocsSignedInDeal,
  checkIsDocsSignedWithOtherSides,
  checkContractFieldsFilled,
  checkDebtRegistryFieldsFilled,
  WatchedFormValues,
  useAutoSaveDealConditions,
} from '../lib';
import { FormTextInput } from '@/shared/inputs/form-text-input';

interface Props {
  data: DealRequest;
  sx?: SxProps;
  openClarificationModal?: ClarificationModalRef['open'];
  callback?: () => void;
  hideActions?: boolean;
}

interface FormValues {
  recourseType: string;
  factorTerm: number | null;
  factorDealAmount: string;
  factorDiscount: string;
  factorPaymentAmount: string;
  factorPaymentDate: Dayjs;
  comissionCalculationTypeId: DefaultDictionary<'id'>;
  repaymentAmount: string;
  isTripartiteContract: boolean;
  factoringContractNumber: string;
  factoringContractDate: Dayjs;
  registerOfDebtsNumber: string;
  registerOfDebtsDate: Dayjs;
}

type ConditionFlag =
  | 'isForExportFactoring'
  | 'isForImportFactoring'
  | 'isForDisclosedFactoring'
  | 'isForUndisclosedFactoring'
  | 'isForReverseFactoring';

const countDiscountSums = (
  sum: string | number,
  discount: string | number,
  commissionCalculationType: string
) => {
  const s = parseFloat(String(sum).replace(/\s/g, ''));
  const d = parseFloat(String(discount).replace(/\s/g, ''));

  if (isNaN(s) || isNaN(d)) return null;

  let paymentAmount = 0;
  const repaymentAmount = (s * d) / 100;

  if (commissionCalculationType === 'insideTheAmount') {
    paymentAmount = s * (1 - d / 100);
    // repaymentAmount = s;
  } else if (commissionCalculationType === 'onTopOfTheAmount') {
    paymentAmount = s;
    // repaymentAmount = s * (1 + d / 100);
  }

  return {
    paymentAmount: paymentAmount.toFixed(2),
    repaymentAmount: repaymentAmount.toFixed(2),
  };
};

export const SingleDealFactorActions: FC<Props> = ({
  sx,
  data,
  openClarificationModal,
  callback,
  hideActions,
}) => {
  const { t } = useTranslation();
  const { profile, isUsbLog } = useAppSelector((state) => state.auth);
  const { showNotification } = useNotification();

  const { control, handleSubmit, reset, setValue, getValues } =
    useForm<FormValues>({
      defaultValues: {
        recourseType: 'nonRecourse',
        factorPaymentDate: dayjs(),
        factoringContractDate: dayjs(),
        registerOfDebtsDate: dayjs(),
      },
    });

  const watchedValues = useWatch({
    control,
    name: [
      'recourseType',
      'factorDealAmount',
      'factorDiscount',
      'factorPaymentAmount',
      'factorPaymentDate',
      'comissionCalculationTypeId',
      'isTripartiteContract',
      'factoringContractNumber',
      'factoringContractDate',
      'registerOfDebtsNumber',
      'registerOfDebtsDate',
    ],
  });

  const watchedFactorTerm = useWatch({ control, name: 'factorTerm' });
  const watchedFactorDiscount = useWatch({ control, name: 'factorDiscount' });

  const {
    data: commissionCalculationTypes,
    isLoading: isCommissionCalculationTypesLoading,
  } = useGetCommissionCalculationTypesQuery();
  const [getCommissionCalculationTypes] =
    useLazyGetCommissionCalculationTypesQuery();

  const [isApprove, setIsApprove] = useState(false);
  const [documents, setDocuments] = useState<(File | null)[]>([null, null]);
  const [isCreateDocLoading, setIsCreateDocLoading] = useState(false);
  const [isRejectLoading, setIsRejectLoading] = useState(false);
  const [isContractSignLoading, setIsContractSignLoading] = useState(false);
  const [isRegistrySignLoading, setIsRegistrySignLoading] = useState(false);
  const [allDocsSignLoading, setAllDocsSignLoading] = useState(false);

  const [rejectRequest] = useRejectDealRequestMutation();

  const [createDoc] = useCreateDealDocumentMutation();
  const [signDoc] = useSignDealDocumentMutation();
  const [getToSign] = useLazyGetStringToSignQuery();
  const [getDocBase64] = useLazyGetDocBase64Query();
  const [signDocument] = useSignDealDocumentMutation();
  const [editDealConditions] = useEditFactorsDealMutation();

  const [deleteDealDocument, { isLoading: isDeleteDocLoading }] =
    useDeleteDealDocumentMutation();

  const { data: recourceTypes, isLoading: isRecourceTypesLoading } =
    useGetRecourseTypesQuery(undefined);

  const commentModalRef = useRef<CommentModalRef>(null);
  const blockEditAfterCreation = useRef(false);
  const debouncedWatchedValues = useDebounce(watchedValues, 500);

  const isContractFieldsFilled = useMemo(
    () => checkContractFieldsFilled(watchedValues as WatchedFormValues),
    [watchedValues]
  );

  const isDebtRegistryFieldsFilled = useMemo(
    () => checkDebtRegistryFieldsFilled(watchedValues as WatchedFormValues),
    [watchedValues]
  );

  const isClosedFactoringDeal = checkIsClosedFactoringDeal(data);
  const isReverseFactoring = data.factoringType === 'reverse';
  // Реверс всегда подписывают 3 стороны - выбор не нужен.
  const showTripartiteSwitch =
    !isClosedFactoringDeal && !data.isInternational && !isReverseFactoring;
  const hasNoDebtRegistry = isClosedFactoringDeal || !!data.isInternational;
  const isContractAndRegistrySent = checkIsContractAndRegistrySentInDeal(data);
  const isDocsSigned = checkIsDocsSignedInDeal(data);
  const isDocsSignedWithOtherSides = checkIsDocsSignedWithOtherSides(data);
  const isDebtRegistryAdded = !!data.debtRegistry?.name;
  const isFactoringContractAdded = !!data.factoringContract?.name;
  const isAllDocsAdded =
    isFactoringContractAdded &&
    (hasNoDebtRegistry ? true : isDebtRegistryAdded);

  const { data: factorsConditions, isLoading: isFactorsConditionsLoading } =
    useGetFactorsConditionsQuery();

  const { data: factorsSettings } = useGetFactorsSettingsQuery(undefined, {
    skip: data.status !== 'active',
    refetchOnMountOrArgChange: true,
  });

  const isTriple =
    factorsSettings?.data.idContractPartiesComposition === 'threeParty';

  useEffect(() => {
    if (!data) return;

    const copyData = async () => {
      const commissionTypes = await getCommissionCalculationTypes(
        undefined,
        true
      ).unwrap();

      const isPercentageOnTopOfAmount =
        factorsSettings?.data?.isPercentageOnTopOfAmount;

      let commissionCalculationType = commissionTypes.data.find(
        (el) => el.id === data.comissionCalculationTypeId
      );

      if (!commissionCalculationType) {
        commissionCalculationType =
          commissionTypes.data.find(
            (el) =>
              el.id ===
              (isPercentageOnTopOfAmount
                ? 'onTopOfTheAmount'
                : 'insideTheAmount')
          ) || commissionTypes.data[0];
      }

      setValue('comissionCalculationTypeId', commissionCalculationType);

      if (isContractAndRegistrySent) {
        reset({
          recourseType: data.recourseTypeId,
          factorDealAmount: String(data.factorDealAmount || 0),
          factorDiscount: String(data.factorDiscount || 0),
          factorTerm: data.factorTerm ?? null,
          factorPaymentAmount: String(data.factorPaymentAmount || 0),
          factorPaymentDate: dayjs(data.factorPaymentDate, 'DD.MM.YYYY'),
          comissionCalculationTypeId: commissionCalculationType,
          isTripartiteContract: !!data.isTripartiteContract,
          repaymentAmount: String(data.factorCommission || 0),
          factoringContractNumber: data.factoringContractNumber || '',
          factoringContractDate: dayjs(
            data.factoringContractDate,
            'DD.MM.YYYY'
          ),
          registerOfDebtsNumber: data.registerOfDebtsNumber || '',
          registerOfDebtsDate: dayjs(data.registerOfDebtsDate, 'DD.MM.YYYY'),
        });

        return;
      }

      setValue('factorTerm', data.factorTerm ?? null);
      if (data.factorDiscount) {
        setValue('factorDiscount', String(data.factorDiscount));

        const sums = countDiscountSums(
          data.factorDealAmount || data.buyerDebtAmount,
          data.factorDiscount,
          commissionCalculationType.id
        );

        if (sums) {
          setValue('factorPaymentAmount', sums.paymentAmount);
          setValue('repaymentAmount', sums.repaymentAmount);
        }
      }
    };

    copyData();
  }, [data, factorsSettings]);

  useEffect(() => {
    if (isTriple === undefined || data.isInternational) return;

    if (data.factoringType === 'opened') {
      setValue('isTripartiteContract', isTriple);
    }
  }, [isTriple, setValue, data.factoringType, data.isInternational]);

  useEffect(() => {
    if (isApprove && !isContractAndRegistrySent) {
      setValue('factorDealAmount', String(data.buyerDebtAmount || ''));
    }
  }, [isApprove]);

  useAutoSaveDealConditions({
    debouncedWatchedValues: debouncedWatchedValues as WatchedFormValues,
    watchedFactorTerm,
    isApprove,
    isContractAndRegistrySent,
    dealId: data.unique,
    blockEditAfterCreation,
  });

  const handleRequestClarifications = () => {
    openClarificationModal?.();
  };

  const handleReject = async () => {
    try {
      setIsRejectLoading(true);
      const reason = await commentModalRef.current?.open({
        title: t('decline_request'),
        submitBtnText: t('reject'),
      });
      const signature = await eimzo.sign(
        profile?.tin || '',
        profile?.tin || '',
        isUsbLog
      );
      if (!reason) return;
      await rejectRequest({ id: data.unique, signature, reason }).unwrap();
      showNotification(t('deal_rejected'), 'success');
    } catch (error) {
      showNotification(getFetchEror(error), 'error');
    } finally {
      setIsRejectLoading(false);
    }
  };

  const handleCreateDoc = async (formValues: FormValues) => {
    try {
      blockEditAfterCreation.current = true;
      setIsCreateDocLoading(true);

      const body = {
        id: data.unique,
        factorDealAmount: Number(
          formValues.factorDealAmount.replace(/\s/g, '')
        ),
        factorTerm: formValues.factorTerm || null,
        factorDiscount: Number(
          String(formValues.factorDiscount).replace(/\s/g, '')
        ),
        factorPaymentAmount: Number(
          formValues.factorPaymentAmount.replace(/\s/g, '')
        ),
        factorPaymentDate: formValues.factorPaymentDate.format('DD/MM/YYYY'),
        recourseTypeId: formValues.recourseType,
        comissionCalculationTypeId: formValues.comissionCalculationTypeId.id,
        isTripartiteContract: formValues.isTripartiteContract || null,
        factoringContractNumber: formValues.factoringContractNumber,
        factoringContractDate: dayjs(formValues.factoringContractDate).format(
          'DD/MM/YYYY'
        ),
        registerOfDebtsNumber: formValues.registerOfDebtsNumber,
        registerOfDebtsDate: dayjs(formValues.registerOfDebtsDate).format(
          'DD/MM/YYYY'
        ),
      };

      await editDealConditions(body).unwrap();

      const docs = await Promise.all(
        documents.map(async (doc) => {
          if (!doc) return null;

          return {
            name: doc.name,
            base64: await getBase64(doc).then((res) => res.split(',')[1]),
          };
        })
      );

      await Promise.all(
        docs.map(async (doc, idx) => {
          if (!doc) return;
          await createDoc({
            id: data.unique,
            base64: doc.base64,
            date: dayjs().format('YYYY.MM.DD'),
            type: idx === 0 ? 'factorContract' : 'debRegistry',
            number: doc.name,
          }).unwrap();
        })
      );

      showNotification(t('deal_approved'), 'success');
      setIsApprove(false);
      callback?.();
      setDocuments([null, null]);
    } catch (err) {
      showNotification(getFetchEror(err), 'error');
    } finally {
      setIsCreateDocLoading(false);
      setTimeout(() => {
        blockEditAfterCreation.current = false;
      }, 1000);
    }
  };

  const handleSignDoc = async (id: string) => {
    try {
      const res = await getToSign({ docId: id, action: 'ownerSign' })
        .unwrap()
        .then((res) => res.data);

      const stringToSign = typeof res === 'string' ? res : JSON.stringify(res);
      const useModern =
        typeof res === 'string'
          ? await eimzo.isModernEimzoFlow(isUsbLog)
          : false;
      const originalString = useModern
        ? await getDocBase64(id)
            .unwrap()
            .then((r) => r.data)
        : undefined;
      const signature = await eimzo.attach(
        stringToSign,
        profile?.tin || '',
        undefined,
        isUsbLog,
        originalString
      );

      if (!signature) {
        throw new Error('Signature is empty');
      }
      await signDoc({ id, signature }).unwrap();
      showNotification(t('document_successfully_signed'), 'success');
    } catch (error) {
      showNotification(getFetchEror(error), 'error');
    }
  };

  const handleSignContract = async (id: string) => {
    try {
      setIsContractSignLoading(true);
      await handleSignDoc(id);
      callback?.();
    } finally {
      setIsContractSignLoading(false);
    }
  };

  const handleSignRegistry = async (id: string) => {
    try {
      setIsRegistrySignLoading(true);
      await handleSignDoc(id);
      callback?.();
    } finally {
      setIsRegistrySignLoading(false);
    }
  };

  const handleSignAllDocs = async () => {
    let chain = Promise.resolve();
    try {
      setAllDocsSignLoading(true);
      const dealDocs = [data.factoringContract, data.debtRegistry];

      if (hasNoDebtRegistry) {
        dealDocs.pop();
      }

      const docIds = dealDocs
        .filter(
          (el) => !el?.signers.find((el) => el.actor === 'factor')?.signedAt
        )
        .map((el) => el?.didoxId);

      const toSigns = await Promise.all(
        docIds.map((el) =>
          getToSign({ docId: el || '', action: 'ownerSign' })
            .unwrap()
            .then((res) => res.data)
        )
      );

      toSigns.forEach((el, index) => {
        chain = chain
          .then(async () => {
            let signature = '';
            if (typeof el === 'string') {
              const docId = dealDocs[index]?.didoxId || '';
              const useModern = await eimzo.isModernEimzoFlow(isUsbLog);
              const originalString = useModern
                ? await getDocBase64(docId)
                    .unwrap()
                    .then((r) => r.data)
                : undefined;
              signature = await eimzo.attach(
                el,
                profile?.tin || '',
                undefined,
                isUsbLog,
                originalString
              );
            } else {
              signature = await eimzo.sign(
                JSON.stringify(el),
                profile?.tin || '',
                isUsbLog
              );
            }

            await signDocument({
              id: dealDocs[index]?.didoxId || '',
              signature,
            }).unwrap();
          })
          .finally(() => {
            if (index === toSigns.length - 1) {
              setAllDocsSignLoading(false);
              showNotification(t('document_successfully_signed'), 'success');
              callback?.();
            }
          })
          .catch((error) => {
            showNotification(getFetchEror(error), 'error');
            setAllDocsSignLoading(false);
          });
      });
    } catch (err) {
      showNotification(getFetchEror(err), 'error');
      setAllDocsSignLoading(false);
    }
  };
  const handleCountDiscountSum = (sum: string, discount: string) => {
    if (sum && discount) {
      const sums = countDiscountSums(
        sum,
        discount,
        getValues('comissionCalculationTypeId').id
      );

      if (!sums) return;

      setValue('factorPaymentAmount', sums.paymentAmount);
      setValue('repaymentAmount', sums.repaymentAmount);
    }
  };

  const deleteDocumentHandler = async (doc: DealRequestDocument) => {
    if (!data.unique) return;
    try {
      await deleteDealDocument({
        dealId: data.unique,
        id: doc.didoxId,
      }).unwrap();
      showNotification(t('document_successfully_deleted'), 'success');
      callback?.();
    } catch (err) {
      showNotification(getFetchEror(err), 'error');
    }
  };

  const dealCreatedAt = dayjs(data.createdAt, 'DD.MM.YYYY HH:mm');

  const showTextOrSelectInputForPercentage = dealCreatedAt.isBefore(
    dayjs('2025-10-17 00:00:00')
  );

  const docsToComare = [data.factoringContract, data.debtRegistry];

  const filteredFactorConditions = useMemo(() => {
    const conditions = factorsConditions?.data ?? [];

    const flag: ConditionFlag | null = (() => {
      switch (getInternationalDealKind(data)) {
        case 'export':
          return 'isForExportFactoring';
        case 'import':
          return 'isForImportFactoring';
        default:
          break;
      }

      switch (data.factoringType) {
        case 'opened':
          return 'isForDisclosedFactoring';
        case 'closed':
          return 'isForUndisclosedFactoring';
        case 'reverse':
          return 'isForReverseFactoring';
        default:
          return null;
      }
    })();

    return flag ? conditions.filter((c) => !!c[flag]) : conditions;
  }, [
    factorsConditions?.data,
    data.factoringType,
    data.isInternational,
    data.isReverseFactoring,
  ]);

  const termOptions = useMemo(() => {
    const parsedDiscount = parseFloat(
      String(watchedFactorDiscount ?? '').replace(/\s/g, '')
    );
    const source =
      !isNaN(parsedDiscount) && parsedDiscount > 0
        ? filteredFactorConditions.filter(
            (c) => c.percentage === parsedDiscount
          )
        : filteredFactorConditions;
    const seen = new Set<number>();
    return source
      .filter((c) => {
        if (seen.has(c.termInDays)) return false;
        seen.add(c.termInDays);
        return true;
      })
      .map((c) => c.termInDays)
      .sort((a, b) => a - b);
  }, [filteredFactorConditions, watchedFactorDiscount]);

  const percentageOptions = useMemo(() => {
    const source =
      watchedFactorTerm != null
        ? filteredFactorConditions.filter(
            (c) => c.termInDays === watchedFactorTerm
          )
        : filteredFactorConditions;
    return [...new Set(source.map((c) => c.percentage))]
      .sort((a, b) => a - b)
      .map(String);
  }, [filteredFactorConditions, watchedFactorTerm]);

  if (hideActions) return null;

  if (isApprove || isContractAndRegistrySent) {
    return (
      <>
        <Box
          mt={2.5}
          sx={{
            backgroundColor: 'rgba(13, 29, 88, 0.04)',
            borderRadius: '12px',
            p: 3,
          }}
        >
          {!isContractAndRegistrySent && (
            <Typography variant='h5' sx={{ mb: 2.5 }}>
              {t('reply_to_applicant')}
            </Typography>
          )}
          <Grid container spacing={2} sx={{ mb: 2.5 }}>
            <Grid item md={4} lg={2}>
              <FormControl
                required={!isContractAndRegistrySent}
                disabled={isContractAndRegistrySent}
              >
                <UIFormLabel>{t('factoring_type')}:</UIFormLabel>
                <FormSelectInput
                  control={control}
                  name='recourseType'
                  rules={{
                    required: true,
                  }}
                  disabled={isContractAndRegistrySent}
                  disableClearable
                  options={recourceTypes?.data?.map((el) => el.id) || []}
                  getOptionLabel={(option) =>
                    recourceTypes?.data?.find((el) => el.id === option)?.name ||
                    ''
                  }
                  loading={isRecourceTypesLoading}
                />
              </FormControl>
            </Grid>

            <Grid item md={4} lg={2}>
              <FormControl
                required={!isContractAndRegistrySent}
                disabled={isContractAndRegistrySent}
              >
                <UIFormLabel>{t('factor_term')}:</UIFormLabel>
                <FormSelectInput
                  control={control}
                  name='factorTerm'
                  rules={{ required: !isContractAndRegistrySent }}
                  disabled={isContractAndRegistrySent}
                  options={termOptions}
                  loading={isFactorsConditionsLoading}
                  clearText={t('clear')}
                  getOptionLabel={(option) =>
                    t('settings.days', { days: option })
                  }
                  TextFieldProps={{ placeholder: t('not_selected') }}
                  onChange={(_, newTerm) => {
                    if (newTerm == null) {
                      setValue('factorDiscount', '');
                      return;
                    }
                    const matches = filteredFactorConditions.filter(
                      (c) => c.termInDays === newTerm
                    );
                    if (matches.length > 0) {
                      const percentage = matches[0].percentage;
                      setValue('factorDiscount', String(percentage));
                      handleCountDiscountSum(
                        getValues('factorDealAmount'),
                        String(percentage)
                      );
                    }
                  }}
                />
              </FormControl>
            </Grid>

            <Grid item md={4} lg={5}>
              <FormControl
                required={!isContractAndRegistrySent}
                disabled={isContractAndRegistrySent}
              >
                <UIFormLabel>{t('percent')}:</UIFormLabel>

                <Stack direction='row' spacing={1}>
                  {showTextOrSelectInputForPercentage ? (
                    <FormNumericInput
                      fullWidth
                      control={control}
                      name='factorDiscount'
                      rules={{ required: true }}
                      disabled={isContractAndRegistrySent}
                      onChange={(event) => {
                        const s = getValues('factorDealAmount');
                        const d = event.target.value;

                        handleCountDiscountSum(s, d);
                      }}
                      sx={{
                        maxWidth: '140px',
                      }}
                    />
                  ) : (
                    <FormSelectInput
                      fullWidth
                      options={percentageOptions}
                      control={control}
                      name='factorDiscount'
                      rules={{ required: true }}
                      disabled={isContractAndRegistrySent}
                      loading={isFactorsConditionsLoading}
                      clearText={t('clear')}
                      onChange={(_, newDiscount) => {
                        if (newDiscount == null) {
                          setValue('factorTerm', null);
                          return;
                        }
                        const s = getValues('factorDealAmount');
                        handleCountDiscountSum(s, String(newDiscount));

                        const percentVal = parseFloat(
                          String(newDiscount).replace(/\s/g, '')
                        );
                        if (!isNaN(percentVal)) {
                          const currentTerm = getValues('factorTerm');
                          const currentTermHasPercent =
                            filteredFactorConditions.some(
                              (c) =>
                                c.termInDays === currentTerm &&
                                c.percentage === percentVal
                            );
                          if (!currentTermHasPercent) {
                            const match = filteredFactorConditions.find(
                              (c) => c.percentage === percentVal
                            );
                            if (match) {
                              setValue('factorTerm', match.termInDays);
                            }
                          }
                        }
                      }}
                      getOptionLabel={(option) => `${option}%`}
                      TextFieldProps={{ placeholder: t('not_selected') }}
                    />
                  )}

                  <FormSelectInput
                    fullWidth
                    options={commissionCalculationTypes?.data || []}
                    control={control}
                    name='comissionCalculationTypeId'
                    disableClearable
                    rules={{ required: true }}
                    disabled={isContractAndRegistrySent}
                    loading={isCommissionCalculationTypesLoading}
                    getOptionLabel={(option) => option.name}
                    onChange={() => {
                      const s = getValues('factorDealAmount');
                      const d = getValues('factorDiscount');

                      handleCountDiscountSum(s, d);
                    }}
                  />
                </Stack>
              </FormControl>
            </Grid>

            <Grid item md={4} lg={3}>
              <FormControl
                required={!isContractAndRegistrySent}
                disabled={isContractAndRegistrySent}
              >
                <UIFormLabel>{t('pay_date')}:</UIFormLabel>
                <FormDatePicker
                  control={control}
                  name='factorPaymentDate'
                  rules={{ required: true }}
                  disabled={isContractAndRegistrySent}
                />
              </FormControl>
            </Grid>

            <Grid item md={4}>
              <FormControl
                required={!isContractAndRegistrySent}
                disabled={isContractAndRegistrySent}
              >
                <UIFormLabel>{t('deal_sum')}:</UIFormLabel>

                <FormNumericInput
                  control={control}
                  name='factorDealAmount'
                  rules={{ required: true }}
                  disabled={isContractAndRegistrySent}
                  onChange={(event) => {
                    const s = event.target.value;
                    const d = getValues('factorDiscount');

                    handleCountDiscountSum(s, d);
                  }}
                />
              </FormControl>
            </Grid>

            <Grid item md={4}>
              <FormControl
                required={!isContractAndRegistrySent}
                disabled={isContractAndRegistrySent}
              >
                <UIFormLabel>{t('pay_sum')}:</UIFormLabel>
                <FormNumericInput
                  control={control}
                  name='factorPaymentAmount'
                  rules={{ required: true }}
                  disabled={isContractAndRegistrySent}
                />
              </FormControl>
            </Grid>

            <Grid item md={4}>
              <FormControl disabled>
                <UIFormLabel>
                  {/* {t(cabinetFlow === CabinetFlow.NBU ? 'commission_sum' : 'redeem_sum')}: */}
                  {t('commission_sum')}
                </UIFormLabel>
                <FormNumericInput
                  control={control}
                  name='repaymentAmount'
                  rules={{ required: true }}
                  disabled
                />
              </FormControl>
            </Grid>
          </Grid>
          {showTripartiteSwitch && (
            <Controller
              control={control}
              name='isTripartiteContract'
              render={({ field }) => (
                <FormControlLabel
                  control={<Switch checked={!!field.value} />}
                  disabled={isContractAndRegistrySent}
                  {...field}
                  label={t('factoring_contract_will_signed_with_3_sides')}
                  sx={{ mb: 2.5 }}
                  slotProps={{
                    typography: {
                      sx: {
                        fontSize: 14,
                      },
                    },
                  }}
                />
              )}
            />
          )}
          <Grid container spacing={2}>
            <Tooltip
              title={
                !isFactoringContractAdded && !!documents[0]
                  ? t('remove_document_tooltip')
                  : ''
              }
              placement='bottom'
            >
              <Grid item xs={12} md={3}>
                <FormControl
                  required
                  fullWidth
                  disabled={
                    isContractAndRegistrySent ||
                    isFactoringContractAdded ||
                    !!documents[0]
                  }
                >
                  <UIFormLabel>{t('factoring_contract_number')}:</UIFormLabel>

                  <FormTextInput
                    control={control}
                    name='factoringContractNumber'
                    placeholder={t('payment_document.notfilled')}
                    rules={{ required: true }}
                    disabled={
                      isContractAndRegistrySent ||
                      isFactoringContractAdded ||
                      !!documents[0]
                    }
                  />
                </FormControl>
              </Grid>
            </Tooltip>
            <Tooltip
              title={
                !isFactoringContractAdded && !!documents[0]
                  ? t('remove_document_tooltip')
                  : ''
              }
              placement='bottom'
            >
              <Grid item xs={12} md={3}>
                <FormControl
                  required
                  fullWidth
                  disabled={
                    isContractAndRegistrySent ||
                    isFactoringContractAdded ||
                    !!documents[0]
                  }
                >
                  <UIFormLabel>{t('factoring_contract_date')}:</UIFormLabel>

                  <FormDatePicker
                    control={control}
                    name='factoringContractDate'
                    rules={{ required: true }}
                    disabled={
                      isContractAndRegistrySent ||
                      isFactoringContractAdded ||
                      !!documents[0]
                    }
                  />
                </FormControl>
              </Grid>
            </Tooltip>

            <Grid item xs={12}>
              <FactorAddDocument
                documentType='contract'
                type='factor'
                label={t('pin_factoring_contract')}
                signLoading={isContractSignLoading || isDeleteDocLoading}
                showSign={
                  // !data.factoringContract?.signers.find(
                  //   (el) => el.actor === "factor"
                  // )?.signedAt && data.factoringContract?.status !== "draft"
                  false
                }
                onSign={() =>
                  handleSignContract(data.factoringContract?.didoxId || '')
                }
                value={
                  isFactoringContractAdded
                    ? data.factoringContract || null
                    : documents[0]
                }
                onChange={(file) => {
                  setDocuments((prev) => {
                    const newState = [...prev];
                    newState[0] = file;
                    return newState;
                  });
                }}
                onDelete={
                  data.factoringContract?.status === 'draft'
                    ? deleteDocumentHandler
                    : undefined
                }
                onDeleteFile={() => {
                  setDocuments((prev) => {
                    const newState = [...prev];
                    newState[0] = null;
                    return newState;
                  });
                }}
                docType='contract'
                isClosedFactoring={data.factoringType === 'closed'}
                isDisabled={!isContractFieldsFilled}
              />
            </Grid>

            {!hasNoDebtRegistry && (
              <>
                <Tooltip
                  title={
                    !isDebtRegistryAdded && !!documents[1]
                      ? t('remove_document_tooltip')
                      : ''
                  }
                  placement='bottom'
                >
                  <Grid item xs={12} md={3}>
                    <FormControl
                      required
                      fullWidth
                      disabled={
                        isContractAndRegistrySent ||
                        isDebtRegistryAdded ||
                        !!documents[1]
                      }
                    >
                      <UIFormLabel>{t('debt_registry_number')}:</UIFormLabel>

                      <FormTextInput
                        control={control}
                        name='registerOfDebtsNumber'
                        placeholder={t('payment_document.notfilled')}
                        rules={{ required: true }}
                        disabled={
                          isContractAndRegistrySent ||
                          isDebtRegistryAdded ||
                          !!documents[1]
                        }
                      />
                    </FormControl>
                  </Grid>
                </Tooltip>
                <Tooltip
                  title={
                    !isDebtRegistryAdded && !!documents[1]
                      ? t('remove_document_tooltip')
                      : ''
                  }
                  placement='bottom'
                >
                  <Grid item xs={12} md={3}>
                    <FormControl
                      required
                      fullWidth
                      disabled={
                        isContractAndRegistrySent ||
                        isDebtRegistryAdded ||
                        !!documents[1]
                      }
                    >
                      <UIFormLabel>{t('debt_registry_date')}:</UIFormLabel>

                      <FormDatePicker
                        control={control}
                        name='registerOfDebtsDate'
                        rules={{ required: true }}
                        disabled={
                          isContractAndRegistrySent ||
                          isDebtRegistryAdded ||
                          !!documents[1]
                        }
                      />
                    </FormControl>
                  </Grid>
                </Tooltip>
                <Grid item xs={12}>
                  <FactorAddDocument
                    documentType='registry'
                    type='factor'
                    label={t('pin_debt_registry')}
                    signLoading={isRegistrySignLoading || isDeleteDocLoading}
                    showSign={
                      // !data.debtRegistry?.signers.find((el) => el.actor === "factor")
                      //   ?.signedAt && data.factoringContract?.status !== "draft"
                      false
                    }
                    onSign={() =>
                      handleSignRegistry(data.debtRegistry?.didoxId || '')
                    }
                    value={
                      isDebtRegistryAdded
                        ? data.debtRegistry || null
                        : documents[1]
                    }
                    onChange={(file) => {
                      setDocuments((prev) => {
                        const newState = [...prev];
                        newState[1] = file;
                        return newState;
                      });
                    }}
                    onDelete={
                      data.debtRegistry?.status === 'draft'
                        ? deleteDocumentHandler
                        : undefined
                    }
                    onDeleteFile={() => {
                      setDocuments((prev) => {
                        const newState = [...prev];
                        newState[1] = null;
                        return newState;
                      });
                    }}
                    docType='registry'
                    isClosedFactoring={data.factoringType === 'closed'}
                    isDisabled={!isDebtRegistryFieldsFilled}
                  />
                </Grid>
              </>
            )}
          </Grid>

          {(!isContractAndRegistrySent || !isAllDocsAdded) && (
            <Stack direction='row' mt={2} justifyContent='flex-end' spacing={2}>
              {!isContractAndRegistrySent && (
                <Button
                  color='inherit'
                  onClick={() => {
                    setIsApprove(false);
                    setDocuments([null, null]);
                  }}
                >
                  {t('cancel')}
                </Button>
              )}

              <Button
                disabled={
                  (hasNoDebtRegistry
                    ? !documents[0]
                    : documents.some(
                        (doc, idx) => !doc && !docsToComare?.[idx]?.name
                      )) || isCreateDocLoading
                }
                onClick={handleSubmit(handleCreateDoc)}
              >
                {t('send_contract')}
              </Button>
            </Stack>
          )}
        </Box>

        {!isDocsSigned &&
          isDocsSignedWithOtherSides &&
          (data.status === 'waitContragentSign' ||
            data.status === 'contractCreated') && (
            <Stack direction='row' justifyContent='flex-end' mt={2}>
              <Button onClick={handleSignAllDocs} disabled={allDocsSignLoading}>
                {t('sign')}
              </Button>
            </Stack>
          )}
      </>
    );
  }

  if (!(data.status === 'active' || data.status === 'underReview')) return null;

  return (
    <Stack spacing={2} sx={sx} direction='row' justifyContent='flex-end'>
      <>
        <Button color='inherit' onClick={handleRequestClarifications}>
          {t('request_clarifications')}
        </Button>
        <Button color='error' onClick={handleReject} disabled={isRejectLoading}>
          {t('reject')}
        </Button>
        <Button
          onClick={() => {
            setIsApprove(true);
          }}
        >
          {t('approve_deal')}
        </Button>
      </>

      <CommentModal ref={commentModalRef} />
    </Stack>
  );
};
